// useAiChatHistory — chat-historik + conversation-persistering för
// AiAssistantDialog (#23 i PRE_RELEASE_REVIEW_100, extraherad ur den
// 1950-raders dialogen).
//
// Äger: conversation-id-ref, one-shot-hydrering från persisterade
// meddelanden, sidebar-val ("öppna gammal chatt"), ny-chatt-reset och
// lazy conversation-skapande (ensureConversation).

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/hooks/use-toast';
import {
  useConversationMutations,
  useConversations,
  useConversationMessages,
} from '@/hooks/useConversations';
import type { Msg } from '@/lib/aiMessageHelpers';

interface UseAiChatHistoryArgs {
  setMessages: (messages: Msg[]) => void;
  /** Rensa transient chat-state (activeFlag, pendingConfirm) vid
   *  conversation-byte/ny chatt så gammal moderation-UI inte hänger kvar. */
  resetTransientState: () => void;
}

export function useAiChatHistory({ setMessages, resetTransientState }: UseAiChatHistoryArgs) {
  // ── Conversation-persistering (Batch CCC) ──────────────────────
  const { createConversation, appendMessage } = useConversationMutations();
  const conversationIdRef = useRef<string | null>(null);
  /**
   * Tracks which conversation id has already been hydrated from
   * `loadedMessages`. Without this, every React Query refetch
   * (triggered by `invalidateMessages` after each `appendMessage`
   * call) re-runs the hydration effect and overwrites the in-memory
   * RAW assistant content with the persisted STRIPPED version —
   * which makes the "Import to canvas" button disappear because
   * `extractJsonBlocks` no longer finds workflow JSON in the
   * stripped content. We hydrate once per conversation id; later
   * refetches are intentionally ignored.
   */
  const hydratedConversationIdRef = useRef<string | null>(null);

  // ── Chat-historik UI (Batch EEE) ───────────────────────────────
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const { data: conversationList } = useConversations('workflow_ai');
  const { data: loadedMessages, error: messagesError } = useConversationMessages(activeConversationId);

  // När user klickar på en gammal conversation i sidebar — ladda dess
  // meddelanden till chat-vyn. Måste rensa direkt på switch (annars visas
  // föregående conversation's meddelanden under fetch-gap), och system-msgs
  // filtreras bort istället för att maskeras som assistant (annars läcker
  // intern prompt-state in i UI:t).
  //
  // Hydration is ONE-SHOT per conversation id. The chat-message
  // persistence layer strips workflow-JSON from assistant content
  // before insert (see `stripJsonBlocks` in AiAssistantDialog) so
  // re-hydrating from `loadedMessages` after a refetch would clobber
  // the in-memory raw content and make the "Import to canvas" button
  // disappear. We only repopulate the first time `loadedMessages`
  // arrives for a given conversation id.
  useEffect(() => {
    if (!activeConversationId) return;
    if (hydratedConversationIdRef.current === activeConversationId) return;
    conversationIdRef.current = activeConversationId;
    if (!loadedMessages) {
      // Initial fetch in flight — wipe the view but don't mark
      // hydrated yet, so the next render (with loadedMessages
      // resolved) actually fills it.
      setMessages([]);
      return;
    }
    hydratedConversationIdRef.current = activeConversationId;
    setMessages(
      loadedMessages
        .filter((m) => m.role !== 'system')
        .map((m) => ({ role: m.role, content: m.content } as Msg)),
    );
  }, [activeConversationId, loadedMessages, setMessages]);

  // C9: Surface:a RPC-fel till user så tyst tomt chat-läge inte sker.
  useEffect(() => {
    if (!messagesError || !activeConversationId) return;
    toast({
      title: 'Kunde inte ladda historik',
      description: `Conversation-RPC:n svarade med fel: ${messagesError.message}. Kontrollera att alla conversation-migrations är applicerade.`,
      variant: 'destructive',
    });
  }, [messagesError, activeConversationId]);

  const handleSelectConversation = useCallback((id: string) => {
    setActiveConversationId(id);
    resetTransientState();
  }, [resetTransientState]);

  const handleNewConversation = useCallback(() => {
    conversationIdRef.current = null;
    hydratedConversationIdRef.current = null;
    setActiveConversationId(null);
    setMessages([]);
    resetTransientState();
  }, [setMessages, resetTransientState]);

  const ensureConversation = useCallback(async (): Promise<string | null> => {
    if (conversationIdRef.current) return conversationIdRef.current;
    const res = await createConversation({ scope: 'workflow_ai' });
    if (res.conversationId) {
      conversationIdRef.current = res.conversationId;
      setActiveConversationId(res.conversationId);
      return res.conversationId;
    }
    // Fallback: om create fails (t.ex. offline) kör vi vidare utan
    // persistering — chat-flödet ska inte blockeras.
    if (res.error === 'history_limit_reached') {
      toast({
        title: 'Gräns nådd',
        description: 'Radera en gammal chatt eller uppgradera för fler.',
        variant: 'destructive',
      });
    }
    return null;
  }, [createConversation]);

  return {
    conversationIdRef,
    activeConversationId,
    conversationList,
    /** Persisterade meddelanden för aktiv conversation (undefined under fetch). */
    loadedMessages,
    appendMessage,
    handleSelectConversation,
    handleNewConversation,
    ensureConversation,
  };
}
